import useQueryBuildings from "ApiLayer/Building/queryBuildings";
import Card from "components/molecules/Card";
import AnnualCashFlowChart from "../AnnualCashFlowChart";

interface MeasureAnnualCashFlowChartRecommendationsProps {
    measure: CarbonReductionMeasure;
    measureAnalysis: MeasureAnalysis;
    backgroundColor?: string;
}

export function MeasureAnnualCashFlowChartRecommendations({
    measure,
    measureAnalysis,
    backgroundColor,
}: MeasureAnnualCashFlowChartRecommendationsProps) {
    let cumulativeNetCashFlow = 0;
    const sortedSummaries = Array.from(measureAnalysis.measureAnalysisYears);
    sortedSummaries.sort((a, b) => a.year - b.year);
    const data = sortedSummaries.map((may: MeasureAnalysisYear, index) => {
        const netCashFlow = may.splitNetSavings - may.splitNetCost;
        cumulativeNetCashFlow += netCashFlow;
        return {
            implementationCost: may.splitImplementationCost,
            netCost: may.splitNetCost,
            totalIncentives: may.incentives,
            totalUtilityCostSavings: may.splitAllUtilityCostSavings,
            totalUtilityCostSavingsElectricity: may.splitElectricityUtilityCostSavings,
            totalUtilityCostSavingsNaturalGas: may.splitNaturalGasUtilityCostSavings,
            totalCarbonPolicyCostSavings: may.carbonCostSavingsAll,
            revenue: may.revenue,
            netSavings: may.splitNetSavings,
            cashFlowAnnual: netCashFlow,
            cashFlowCumulative: cumulativeNetCashFlow,
            year: may.year,
            landlordShareCost: may.landlordShareCost,
            landlordShareElectricity: may.landlordShareElectricity,
            landlordShareNaturalGas: may.landlordShareNaturalGas,
            landlordShareSteam: may.landlordShareSteam,
        };
    });

    return (
        <AnnualCashFlowChart
            query={data as any[]}
            tableColummnKeys={[
                "year",
                "implementationCost",
                "totalIncentives",
                "netCost",
                "landlordShareCost",
                "totalUtilityCostSavingsElectricity",
                "landlordShareElectricity",
                "totalUtilityCostSavingsNaturalGas",
                "landlordShareNaturalGas",
                "totalUtilityCostSavings",
                "totalCarbonPolicyCostSavings",
                "revenue",
                "netSavings",
                "cashFlowAnnual",
                "cashFlowCumulative",
            ]}
            barKeys={[
                "totalUtilityCostSavingsElectricity",
                "totalUtilityCostSavingsNaturalGas",
                "totalCarbonPolicyCostSavings",
                "revenue",
                "totalIncentives",
                "implementationCost",
            ]}
            height={460}
            backgroundColor={backgroundColor}
            parentComponent="AnnualCashFlowChartRecommendations"
        />
    );
}

interface BuildingAnnualCashFlowChartRecommendationsProps {
    buildingModel: Building;
    selectedBMCRP: BuildingModelCarbonReductionPlan;
    backgroundColor?: string;
}

export function BuildingAnnualCashFlowChartRecommendations({
    buildingModel,
    selectedBMCRP,
    backgroundColor,
}: BuildingAnnualCashFlowChartRecommendationsProps) {
    const crp: CarbonReductionPlan = selectedBMCRP.plan;

    let cumulativeNetCashFlow = 0;
    const sortedSummaries = Array.from(crp.annualSummaries);
    sortedSummaries.sort((a, b) => a.year - b.year);
    const data = sortedSummaries.map((asum: AnnualSummary) => {
        const netCashFlow = asum.splitNetSavings - asum.splitNetIncrementalCost;
        cumulativeNetCashFlow += netCashFlow;
        return {
            totalIncrementalCost: asum.splitIncrementalMeasureCost,
            totalLikeForLikeCost: asum.splitTotalMeasureCost - asum.splitIncrementalMeasureCost,
            totalMeasureCost: asum.splitTotalMeasureCost,
            utilityRatesElectricity: asum.utilityRatesElectricity,
            utilityRatesNaturalGas: asum.utilityRatesNaturalGas,
            utilityCostElectricity: asum.utilityCostElectricity,
            utilityCostNaturalGas: asum.utilityCostNaturalGas,
            totalUtilityCostSavings: asum.splitAnnualUtilityCostSavings,
            annualUtilityCostSavingsElectricity: asum.splitUtilityCostSavingsElectricity,
            annualUtilityCostSavingsNaturalGas: asum.splitUtilityCostSavingsNaturalGas,
            totalCarbonPolicyCostSavings: asum.carbonPolicySavings,
            carbonPolicyCost: asum.carbonPolicyCosts,
            totalIncentives: asum.incentives,
            cashFlowAnnual: netCashFlow,
            cashFlowCumulative: cumulativeNetCashFlow,
            netIncrementalCost: asum.splitNetIncrementalCost,
            netTotalCost: asum.splitNetTotalCost,
            netSavings: asum.splitNetSavings,
            splitNetIncrementalCost: asum.splitNetIncrementalCost,
            splitNetTotalCost: asum.splitNetTotalCost,
            splitNetSavings: asum.splitNetSavings,
            revenue: asum.revenue,
            year: asum.year,
        };
    });

    return (
        <AnnualCashFlowChart
            query={data as any[]}
            tableColummnKeys={[
                "year",
                "totalIncrementalCost",
                "totalLikeForLikeCost",
                "totalMeasureCost",
                "totalIncentives",
                "netIncrementalCost",
                "netTotalCost",
                "annualUtilityCostSavingsElectricity",
                "annualUtilityCostSavingsNaturalGas",
                "totalUtilityCostSavings",
                "totalCarbonPolicyCostSavings",
                "revenue",
                "netSavings",
                "utilityRatesElectricity",
                "utilityRatesNaturalGas",
                "carbonPolicyCost",
                "utilityCostElectricity",
                "utilityCostNaturalGas",
                "cashFlowAnnual",
                "cashFlowCumulative",
            ]}
            barKeys={[
                "totalIncrementalCost",
                "totalLikeForLikeCost",
                "totalUtilityCostSavings",
                "totalCarbonPolicyCostSavings",
                "totalIncentives",
                "revenue",
            ]}
            height={460}
            backgroundColor={backgroundColor}
            parentComponent="AnnualCashFlowChartRecommendations"
        />
    );
}

interface ReportsAnnualCashFlowChartRecommendationsProps {
    filtersetUid: string;
    reportedVsPublished: string;
    backgroundColor?: string;
}

export function ReportsAnnualCashFlowChartRecommendations({
    filtersetUid,
    reportedVsPublished,
    backgroundColor,
}: ReportsAnnualCashFlowChartRecommendationsProps) {
    const { isLoading, data: buildingsResponse } = useQueryBuildings(filtersetUid, reportedVsPublished);

    if (isLoading) {
        return <Card loading={true} cardTitle="Annual Cash Flow Chart" />;
    }

    const summariesByYear = new Map<number, AnnualSummary[]>();
    buildingsResponse!.buildings.forEach((building) => {
        const crp: CarbonReductionPlan = building.reportedCrp!;
        crp.annualSummaries.forEach((aSum) => {
            const aSums = summariesByYear.get(aSum.year) ?? [];
            aSums.push(aSum);
            summariesByYear.set(aSum.year, aSums);
        });
    });
    const allYears = Array.from(summariesByYear.keys());
    allYears.sort();

    let cumulativeNetCashFlow = 0;
    const data = allYears.map((year) => {
        const rv = {
            totalIncrementalCost: 0,
            totalLikeForLikeCost: 0,
            totalUtilityCostSavings: 0,
            annualUtilityCostSavingsElectricity: 0,
            annualUtilityCostSavingsNaturalGas: 0,
            totalCarbonPolicyCostSavings: 0,
            totalIncentives: 0,
            cashFlowAnnual: 0,
            cashFlowCumulative: 0,
            netIncrementalCost: 0,
            presentValue: 0, // NPV isn't displayed anyway
            revenue: 0,
            year,
        };
        summariesByYear.get(year)!.forEach((aSum: AnnualSummary) => {
            const netCashFlow = aSum.splitNetSavings - aSum.splitNetIncrementalCost;
            cumulativeNetCashFlow += netCashFlow;
            rv.totalIncrementalCost += aSum.splitIncrementalMeasureCost;
            rv.totalLikeForLikeCost += aSum.splitTotalMeasureCost - aSum.splitIncrementalMeasureCost;
            rv.totalUtilityCostSavings += aSum.splitAnnualUtilityCostSavings;
            rv.annualUtilityCostSavingsElectricity += aSum.splitUtilityCostSavingsElectricity;
            rv.annualUtilityCostSavingsNaturalGas += aSum.splitUtilityCostSavingsNaturalGas;
            rv.totalCarbonPolicyCostSavings += aSum.carbonPolicySavings;
            rv.totalIncentives += aSum.incentives;
            rv.cashFlowAnnual += netCashFlow;
            rv.cashFlowCumulative = cumulativeNetCashFlow;
            rv.netIncrementalCost += aSum.splitNetIncrementalCost;
            rv.revenue += aSum.revenue;
        });
        return rv;
    });
    return (
        <AnnualCashFlowChart
            query={data as any[]}
            tableColummnKeys={[
                "year",
                "totalIncrementalCost",
                "totalLikeForLikeCost",
                "totalIncentives",
                "netIncrementalCost",
                "totalUtilityCostSavings",
                "annualUtilityCostSavingsElectricity",
                "annualUtilityCostSavingsNaturalGas",
                "totalCarbonPolicyCostSavings",
                "revenue",
                "cashFlowAnnual",
                "cashFlowCumulative",
            ]}
            barKeys={[
                "totalIncrementalCost",
                "totalLikeForLikeCost",
                "totalIncentives",
                "totalUtilityCostSavings",
                "totalCarbonPolicyCostSavings",
                "revenue",
            ]}
            height={460}
            backgroundColor={backgroundColor}
            parentComponent="AnnualCashFlowChartRecommendations"
        />
    );
}
